
import { defineComponent, nextTick, onBeforeUnmount, onMounted, PropType, reactive, ref, watch } from 'vue';
import flatPickr from 'vue-flatpickr-component';
import { useI18n } from 'vue-i18n';
import { DateFormatter } from "@/utilities/date-formatter";

export default defineComponent({
    components: {
        flatPickr
    },

    props: {
        withTime: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: null as unknown as PropType<Date|null>,
            required: true,
            validator(this: void, prop: Date|null) {
                return prop instanceof Date || prop === null;
            },
        },
        required: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        "update:modelValue",
    ],

    setup(props, {emit})
    {
        const i18n = useI18n();

        // This ref will allow to call internal methods of the vue-flatpickr component.
        // Not a good practice but in this case, because it is an external library, we cannot do better.
        const vueFlatPickrInstance = ref<InstanceType<typeof flatPickr>>();

        const config = reactive({
            allowInput: true,
            altInput: true,
            altFormat: props.withTime ? "d/m/Y H:i" : "d/m/Y",
            clickOpens: false,
            enableTime: props.withTime,
            time_24hr: true,
            wrap: true,
        });

        function modelValueToInternalValue(modelValue: Date|null)
        {
            const formatter = new DateFormatter();

            if (modelValue === null) {
                return null;
            }

            return props.withTime ? formatter.isoDateTime(modelValue) : formatter.isoDate(modelValue);
        }

        function getFpInput() : HTMLInputElement|undefined
        {
            return vueFlatPickrInstance.value.fpInput() as HTMLInputElement|undefined;
        }

        // Sync the internal value with the model value (which must be transformed into a date object)
        const internalValue = ref<string|null>(modelValueToInternalValue(props.modelValue));

        watch(() => props.modelValue, (newValue) => {
            internalValue.value = modelValueToInternalValue(newValue);
        });
        watch(internalValue, (newValue) => {
            emit("update:modelValue", newValue ? new Date(newValue) : null);
        });

        // Set the DOM field's "required" attribute when the component property changes
        watch(() => props.required, (newValue) => {
            updateFieldRequiredAttribute(newValue);
        });

        // Hook into the component lifecycle to 
        onMounted(() => {
            // As per Vue documentation, deferring the update on the next tick after onMounted
            // ensures that the child component we need to wait for is also mounted.
            nextTick(() => {
                updateFieldRequiredAttribute(props.required);

                // Add a "data-lpignore" attribute to prevent LastPass password manager to think it should allow to fill it
                getFpInput()?.setAttribute("data-lpignore", "true");

                // Watch the user typing so we can catch the "Enter" key press
                getFpInput()?.addEventListener("keydown", handleUserInput);
            });
        });

        onBeforeUnmount(() => {
            getFpInput()?.removeEventListener("keydown", handleUserInput);
        });

        function handleUserInput(e: KeyboardEvent)
        {
            // Submit form by clicking on the submit button if the enter key is pressed.
            // This is different from just calling sumbit() on the form because directly submitting
            // would bypass validation and event handlers listening to it.
            if (e.key === "Enter") {
                const submitButton = getFpInput()?.form?.querySelector("button[type='submit']") as HTMLButtonElement|null;
                submitButton?.click();
            }

        }

        function handleValueChange(newValue: string|null)
        {
            internalValue.value = newValue;

            updateValidation(newValue ? new Date(newValue) : null);
        }

        function updateFieldRequiredAttribute(newValue: boolean)
        {
            const input = getFpInput();

            if (input) {
                input.required = newValue;
            }
        }

        function updateValidation(newValue: Date|null)
        {
            const input = getFpInput();
            
            // Clear error state if the value is valid
            if (!props.required || newValue !== null) {
                input?.setCustomValidity("");
                return;
            }

            // Validation error
            input?.setCustomValidity(i18n.t('validationMessage.required'));
        }

        return { vueFlatPickrInstance, config, internalValue, handleValueChange, updateValidation };
    },
})
